import type { ImageStyle } from '../../lib/postTypes'
import { applyToggle, type MixEntry } from '../../lib/weightedMix'
import { WeightedMixEditor } from './WeightedMixEditor'
import { KomaPanel } from './KomaPanel'
import type { SectionProps } from './types'

// スタイルのサンプル画像（実際にこのアプリのパイプラインで生成した実例）
// images/styles/<styleKey>.jpg を一括ロードし、キー = ファイル名で引く
const STYLE_SAMPLES: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob('../../images/styles/*.jpg', { eager: true, import: 'default' }) as Record<string, string>,
  ).map(([path, url]) => [path.replace(/^.*\//, '').replace(/\.jpg$/, ''), url]),
)

export function ImageSection({ draft, setDraft, styles }: SectionProps & { styles: ImageStyle[] }) {
  const mix: MixEntry[] = (draft.image.styleMix ?? []).map(m => ({ key: m.style, weight: m.weight }))
  const selectedKeys = mix.length > 0 ? mix.map(m => m.key) : [draft.image.style]
  const labelOf = (key: string) => styles.find(s => s.key === key)?.label ?? key

  const writeMix = (style: string, entries: MixEntry[]) =>
    setDraft({
      ...draft,
      image: { ...draft.image, style, styleMix: entries.map(e => ({ style: e.key, weight: e.weight })) },
    })

  const toggleStyle = (key: string) => {
    const next = applyToggle(draft.image.style, mix, key)
    if (next) writeMix(next.single, next.mix)   // null = 最後の1つは外させない
  }

  return (
    <div className="space-y-4">
      <div className="gm-card p-4 md:p-5">
        <p className="font-bold">投稿画像の自動生成</p>
        <p className="mt-1 text-sm text-ink-muted">
          オンにすると、投稿ごとに本文に合った画像を1枚生成して添付します（+約1.5cr/枚。コマ割りの四角は約3cr）。画像付き投稿はタイムラインで目に留まりやすくなります。
        </p>
        <label className="mt-3 flex items-center gap-2 text-sm" data-testid="image-enabled">
          <input type="checkbox" className="accent-primary" checked={draft.image.enabled}
            onChange={e => setDraft({ ...draft, image: { ...draft.image, enabled: e.target.checked } })} />
          投稿に画像を付ける
        </label>
      </div>

      {/* コマ割り（4コマ）モード */}
      <KomaPanel draft={draft} setDraft={setDraft} />

      <div className="gm-card p-4 md:p-5">
        <p className="font-bold">画像のスタイル</p>
        {!draft.image.enabled && <p className="mt-1"><span className="gm-chip gm-chip-neutral">画像生成がオフのため、今は使われません</span></p>}
        <p className="mt-1 text-sm text-ink-muted">
          お店の雰囲気に合うテイストを選んでください。
          複数選ぶと、投稿ごとに指定した確率でランダムに使い分けます（執筆の型と同じ方式）。
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {styles.map(s => {
            const active = selectedKeys.includes(s.key)
            const weight = mix.find(m => m.key === s.key)?.weight
            return (
              <button key={s.key} data-testid={`image-style-${s.key}`} aria-pressed={active}
                disabled={!draft.image.enabled}
                onClick={() => toggleStyle(s.key)}
                className={`relative overflow-hidden rounded-card border text-left transition-colors ${
                  active ? 'border-primary bg-primary-subtle ring-1 ring-primary' : 'border-line hover:border-line-strong'
                } disabled:cursor-not-allowed`}>
                {weight != null && (
                  <span className="gm-num absolute right-2 top-2 rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-[rgb(var(--gm-on-primary))]">
                    {weight}%
                  </span>
                )}
                {STYLE_SAMPLES[s.key] && (
                  <img src={STYLE_SAMPLES[s.key]} alt={s.label}
                    className="aspect-video w-full object-cover" />
                )}
                <div className="p-3">
                  <p className={`text-sm font-bold ${active ? 'text-primary' : ''}`}>{s.label}</p>
                  <p className="mt-0.5 text-xs text-ink-muted">{s.description}</p>
                </div>
              </button>
            )
          })}
        </div>

        {mix.length > 0 && (
          <WeightedMixEditor title="スタイルの配分" testId="style-mix-editor" entries={mix} labelOf={labelOf}
            labelClassName="w-44" onChange={entries => writeMix(draft.image.style, entries)} />
        )}
      </div>
    </div>
  )
}
